import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const categories = [
  { id: 'all', label: 'All' },
  { id: 'lrs', label: 'LRS' },
  { id: 'bps', label: 'BPS' },
  { id: 'building', label: 'Building Permission' },
  { id: 'masterplan', label: 'Master Plan' },
  { id: 'circulars', label: 'Circulars' },
];

const gosData = [
  {
    category: 'lrs',
    goNumber: 'G.O. Ms. No. 131',
    date: '2020-09-01',
    title: 'Layout Regularisation Scheme (LRS) - 2020',
    department: 'MA&UD Department',
    description: 'Guidelines for regularisation of unapproved and illegal layouts under LRS-2020 scheme.',
  },
  {
    category: 'lrs',
    goNumber: 'G.O. Ms. No. 151',
    date: '2020-10-15',
    title: 'LRS-2020 - Extension of Time & Fee Structure Revision',
    department: 'MA&UD Department',
    description: 'Extension of time period for LRS applications and revised fee structure.',
  },
  {
    category: 'lrs',
    goNumber: 'G.O. Ms. No. 62',
    date: '2021-03-20',
    title: 'LRS - Additional Guidelines for Pending Applications',
    department: 'MA&UD Department',
    description: 'Additional guidelines for processing pending LRS applications and clarifications on disputed cases.',
  },
  {
    category: 'bps',
    goNumber: 'G.O. Ms. No. 119',
    date: '2020-08-10',
    title: 'Building Penalisation Scheme (BPS) - 2020',
    department: 'MA&UD Department',
    description: 'Scheme for penalisation of buildings constructed in violation of approved building plan or without permission.',
  },
  {
    category: 'bps',
    goNumber: 'G.O. Ms. No. 245',
    date: '2021-06-05',
    title: 'BPS - Revised Penalty Calculation Methodology',
    department: 'MA&UD Department',
    description: 'Revised methodology for calculation of penalisation charges based on nature and extent of violations.',
  },
  {
    category: 'building',
    goNumber: 'G.O. Ms. No. 168',
    date: '2017-04-28',
    title: 'A.P. Building Rules, 2017 - Notification',
    department: 'MA&UD Department',
    description: 'Notification of comprehensive building rules governing construction standards, setbacks, FAR, and safety norms.',
  },
  {
    category: 'building',
    goNumber: 'G.O. Ms. No. 189',
    date: '2019-07-15',
    title: 'Online Building Permission System (OBPS) - Implementation',
    department: 'MA&UD Department',
    description: 'Implementation of online building permission system through APDPMS portal for all ULBs and UDAs.',
  },
  {
    category: 'building',
    goNumber: 'G.O. Ms. No. 96',
    date: '2022-02-12',
    title: 'Amendments to A.P. Building Rules, 2017',
    department: 'MA&UD Department',
    description: 'Amendments to building rules regarding high-rise buildings, green building norms, and fire safety compliance.',
  },
  {
    category: 'masterplan',
    goNumber: 'G.O. Ms. No. 520',
    date: '2018-11-22',
    title: 'Master Plan for Visakhapatnam Metropolitan Region',
    department: 'MA&UD Department',
    description: 'Notification of Master Plan for Visakhapatnam Metropolitan Region with zoning regulations and development norms.',
  },
  {
    category: 'masterplan',
    goNumber: 'G.O. Ms. No. 328',
    date: '2019-09-10',
    title: 'Master Plan for Vijayawada-Guntur-Tenali-Mangalagiri (VGTM) Region',
    department: 'MA&UD Department',
    description: 'Revision and notification of Master Plan for VGTM Urban Development Area.',
  },
  {
    category: 'masterplan',
    goNumber: 'G.O. Ms. No. 215',
    date: '2020-05-18',
    title: 'Master Plan for Tirupati Urban Development Area',
    department: 'MA&UD Department',
    description: 'Notification of comprehensive Master Plan for Tirupati Urban Development Area with land use proposals.',
  },
  {
    category: 'circulars',
    goNumber: 'Circular No. 12/2023',
    date: '2023-04-15',
    title: 'Standard Operating Procedure for Building Permission Processing',
    department: 'DTCP',
    description: 'Standardized procedure for processing building permissions within timelines across all UDAs.',
  },
  {
    category: 'circulars',
    goNumber: 'Circular No. 8/2023',
    date: '2023-03-01',
    title: 'Directions on Unauthorized Construction Monitoring',
    department: 'DTCP',
    description: 'Directions to all UDAs and municipal bodies regarding monitoring and prevention of unauthorized constructions.',
  },
  {
    category: 'circulars',
    goNumber: 'Circular No. 21/2024',
    date: '2024-01-10',
    title: 'Digital Records Maintenance & APDPMS Usage',
    department: 'DTCP',
    description: 'Directions for mandatory use of APDPMS portal and digital maintenance of all building permission records.',
  },
  {
    category: 'circulars',
    goNumber: 'Circular No. 5/2024',
    date: '2024-06-20',
    title: 'Revised Timeline for Layout Approval Processing',
    department: 'DTCP',
    description: 'Revised timelines for processing layout approvals and mandated weekly reporting on pending applications.',
  },
];

export const GosCircularsPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = activeCategory === 'all'
    ? gosData
    : gosData.filter((g) => g.category === activeCategory);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <a href="/" className="hover:text-amber-700 transition-colors">{t('header.home')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <a href="/resources" className="hover:text-amber-700 transition-colors">{t('header.resources')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <span className="text-stone-800 font-medium">{t('header.resourcesGosCirculars')}</span>
      </nav>

      {/* Page Header */}
      <div className="mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">{t('header.resourcesGosCirculars')}</h1>
        <p className="text-lg text-stone-600 max-w-3xl">
          Government Orders, circulars, and notifications issued by DTCP and the Government of Andhra Pradesh related to town planning and urban development.
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-8">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              activeCategory === cat.id
                ? 'bg-amber-600 text-white shadow-sm'
                : 'bg-stone-100 text-stone-600 hover:bg-amber-100 hover:text-amber-700'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* GO List */}
      <div className="space-y-4">
        {filtered.map((go, index) => (
          <div
            key={index}
            className="group bg-white border border-stone-200 rounded-lg p-5 hover:shadow-md hover:border-amber-300 transition-all duration-200"
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-800">
                    {go.goNumber}
                  </span>
                  <span className="text-xs text-stone-400">{go.department}</span>
                </div>
                <h3 className="text-stone-800 font-semibold group-hover:text-amber-700 transition-colors mb-1">
                  {go.title}
                </h3>
                <p className="text-sm text-stone-500 leading-relaxed">{go.description}</p>
              </div>
              <div className="flex items-center gap-3 md:flex-shrink-0">
                <span className="text-sm text-stone-400 whitespace-nowrap">
                  <i className="ri-calendar-line mr-1"></i>
                  {new Date(go.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
